import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTrialInfo } from '../../hooks/useFeatureGate';
import { useSubscription } from '../../contexts/SubscriptionContext';

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Banner = styled.div<{ $urgent: boolean; $expired: boolean }>`
  background: ${props => {
    if (props.$expired) return 'linear-gradient(90deg, #c62828 0%, #b71c1c 100%)';
    if (props.$urgent) return 'linear-gradient(90deg, #f57c00 0%, #e65100 100%)';
    return 'linear-gradient(90deg, #00838f 0%, #006064 100%)';
  }};
  color: #fff;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  font-size: 0.95rem;
  animation: ${slideDown} 0.3s ease;
  position: relative;
  z-index: 100;
`;

const Message = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DaysCount = styled.span`
  font-weight: 700;
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
`;

const UpgradeButton = styled.button`
  background: rgba(255, 255, 255, 0.95);
  color: #0d1a26;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fff;
    transform: scale(1.02);
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  position: absolute;
  right: 12px;

  &:hover {
    color: #fff;
  }
`;

interface TrialBannerProps {
  onUpgrade?: () => void;
}

const TrialBanner: React.FC<TrialBannerProps> = ({ onUpgrade }) => {
  const { isTrialing, trialDaysRemaining, trialExpired, loading } = useTrialInfo();
  const { redirectToCheckout, plans, loadPlans } = useSubscription();
  const [dismissed, setDismissed] = useState(false);

  // Не показываем если загрузка, не в триале, или баннер закрыт
  if (loading || dismissed) return null;
  if (!isTrialing && !trialExpired) return null;

  const isUrgent = (trialDaysRemaining ?? 0) <= 7 && (trialDaysRemaining ?? 0) > 0;

  const handleUpgrade = async () => {
    if (onUpgrade) {
      onUpgrade();
      return;
    }

    // Загрузим планы если нужно
    if (!plans) {
      await loadPlans();
    }

    const proPlan = plans?.find(p => p.name === 'pro');
    if (proPlan) {
      await redirectToCheckout(proPlan.id, 'monthly');
    }
  };

  if (trialExpired) {
    return (
      <Banner $urgent={false} $expired={true}>
        <Message>
          <span role="img" aria-label="warning">&#x26A0;&#xFE0F;</span>
          Your trial has expired. Upgrade now to continue using premium features.
        </Message>
        <UpgradeButton onClick={handleUpgrade}>
          Upgrade Now
        </UpgradeButton>
        <CloseButton onClick={() => setDismissed(true)} title="Dismiss">
          &times;
        </CloseButton>
      </Banner>
    );
  }

  return (
    <Banner $urgent={isUrgent} $expired={false}>
      <Message>
        {isUrgent ? (
          <>
            <span role="img" aria-label="clock">&#x23F0;</span>
            Your trial ends in <DaysCount>{trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}</DaysCount>
          </>
        ) : (
          <>
            <span role="img" aria-label="info">&#x2139;&#xFE0F;</span>
            You're on a free trial - <DaysCount>{trialDaysRemaining} days</DaysCount> remaining
          </>
        )}
      </Message>
      <UpgradeButton onClick={handleUpgrade}>
        {isUrgent ? 'Upgrade Now' : 'Upgrade to Pro'}
      </UpgradeButton>
      <CloseButton onClick={() => setDismissed(true)} title="Dismiss">
        &times;
      </CloseButton>
    </Banner>
  );
};

export default TrialBanner;
